import React from 'react';

const StockComparison = ({ stocks }) => {
  if (!stocks || stocks.length === 0) {
    return <div>No stocks to compare</div>;
  }

  // Helper function to ensure a value is a number
  const ensureNumber = (value, defaultValue = 0) => {
    if (value === null || value === undefined || value === '') return defaultValue;
    const num = typeof value === 'number' ? value : parseFloat(value);
    return isNaN(num) ? defaultValue : num;
  };

  // Format numbers with appropriate suffixes
  const formatLargeNumber = (value) => {
    if (value === null || value === undefined) return 'N/A';
    
    const num = ensureNumber(value);
    
    if (Math.abs(num) >= 1000000000) {
      return `$${(num / 1000000000).toFixed(2)}B`;
    }
    
    if (Math.abs(num) >= 1000000) {
      return `$${(num / 1000000).toFixed(2)}M`;
    }
    
    return `$${num.toFixed(2)}`;
  };

  // Format percentages
  const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A';
    return `${ensureNumber(value).toFixed(2)}`;
  };

  // Format date more readably
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    } catch (e) {
      return dateString;
    }
  };

  // Get value safely from stock data with nested object support
  const getValue = (stock, path, formatter = (x) => x) => {
    if (!stock) return 'N/A';
    
    // Handle dot notation paths like 'dividend.yield'
    const pathParts = path.split('.');
    let value = stock;
    
    for (const part of pathParts) {
      if (value === null || value === undefined) return 'N/A';
      value = value[part];
    }
    
    if (value === null || value === undefined) return 'N/A';
    return formatter(value);
  };

  // Define comparison metrics
  const metrics = [
    { label: 'STOCK', path: 'name' },
    { label: 'PRICE', path: 'price', format: (val) => `$${ensureNumber(val).toFixed(2)}` },
    { label: 'MARKET CAP.', path: 'market_cap', format: formatLargeNumber },
    { label: 'PE RATIO', path: 'pe_ratio', format: formatPercent },
    { label: 'EPS', path: 'eps', format: (val) => val != null ? `$${ensureNumber(val).toFixed(2)}` : 'N/A' },
    { label: 'DIVIDEND YIELD', path: 'dividend_yield', format: (val) => val != null ? `${ensureNumber(val).toFixed(2)}%` : 'N/A' },
    { label: 'LAST DIVIDEND', path: 'lastDividend', format: (val) => {
      if (!val || !val.amount || !val.date) return 'N/A';
      return `$${ensureNumber(val.amount).toFixed(2)} on ${formatDate(val.date)}`;
    }},
    { label: 'ANNUAL REVENUE', path: 'annual_revenue', format: formatLargeNumber },
    { label: 'QUARTERLY REVENUE', path: 'quarterly_revenue', format: formatLargeNumber },
    { label: 'ANNUAL EARNINGS', path: 'annual_earnings', format: formatLargeNumber },
    { label: 'QUARTERLY EARNINGS', path: 'quarterly_earnings', format: formatLargeNumber },
    { label: 'CASH', path: 'cash', format: formatLargeNumber },
    { label: 'LIABILITIES / ASSETS', path: 'liabilities_to_assets', format: (val) => val != null ? `${ensureNumber(val * 100).toFixed(1)}%` : 'N/A' },
    { label: 'GROSS MARGIN', path: 'gross_margin', format: (val) => val != null ? `${ensureNumber(val).toFixed(1)}%` : 'N/A' },
    { label: 'NET MARGIN', path: 'net_profit_margin', format: (val) => val != null ? `${ensureNumber(val).toFixed(1)}%` : 'N/A' },
    { label: 'RETURN ON EQUITY', path: 'return_on_equity', format: (val) => val != null ? `${ensureNumber(val).toFixed(1)}%` : 'N/A' },
    { label: 'DEBT / EQUITY', path: 'debt_to_equity', format: formatPercent },
  ];

  return (
    <div className="stock-comparison">
      <h2>Stock Comparison</h2>
      
      <div className="comparison-table-container">
        <table className="comparison-table">
          <thead>
            <tr>
              <th className="metric-column">METRIC</th>
              {stocks.map((stock, index) => (
                <th key={index} className="stock-column">
                  {stock.symbol || `Stock ${index + 1}`}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {metrics.map((metric, metricIndex) => (
              <tr key={metricIndex}>
                <td className="metric-label">{metric.label}</td>
                {stocks.map((stock, stockIndex) => (
                  <td key={stockIndex} className="metric-value">
                    {metric.format ? metric.format(stock[metric.path]) : getValue(stock, metric.path)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StockComparison;